import React from 'react';

/**
 * Nia logo — a custom mark.
 *
 * Concept:
 *  - "Nia" means "purpose / intention" in Swahili.
 *  - The mark is a geometric N with an organic leaf sprout in gold on the top-right —
 *    representing growth and a family taking root, the senders' purposeful support.
 *  - Rounded square in deep brand green; clean cap-strokes; one gold accent.
 *
 * Variants:
 *  - <NiaMark size={N} /> — just the icon (square mark)
 *  - <NiaLogo size={N} variant="light|dark" /> — icon + wordmark
 */
export function NiaMark({ size = 44, bg = 'var(--nia-dark)', stroke = '#fff', accent = 'var(--nia-gold)', radius = 12 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Nia">
            <rect width="56" height="56" rx={radius} fill={bg} />
            {/* N letterform — three precise strokes with rounded caps */}
            <path
                d="M16 41 V17 M16 17 L40 41 M40 17 V41"
                stroke={stroke}
                strokeWidth="4.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
            />
            {/* Gold leaf/sprout accent on top-right of N (growth, purpose, home) */}
            <path
                d="M42.4 12.6 C45.6 13.5 47.4 15.6 47.4 18.8 C45.4 18.8 43.6 17.7 42.5 16 C41.6 17.6 39.8 18.5 38 18.4 C38 15.6 39.6 13.5 42.4 12.6 Z"
                fill={accent}
            />
            {/* tiny stem dot */}
            <circle cx="40.5" cy="19.4" r="1.4" fill={accent} />
        </svg>
    );
}

export function NiaLogo({ size = 36, variant = 'dark', includeWordmark = true }) {
    const wordmarkColor = variant === 'light' ? '#fff' : 'var(--nia-text)';
    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <NiaMark size={size} />
            {includeWordmark && (
                <span
                    className="font-display"
                    style={{
                        fontSize: size * 0.78,
                        fontWeight: 700,
                        color: wordmarkColor,
                        letterSpacing: '-0.05em',
                        lineHeight: 1,
                        position: 'relative',
                        top: 1,
                    }}
                >
                    Nia
                </span>
            )}
        </div>
    );
}

export default NiaLogo;
